import { chunk } from 'lodash'
import {
    parseDateString, parseTimeString, validation, _
} from 'utils'
import * as Yup from 'yup'
import { setProgress } from 'features/export_import/importExportSlice'
import {
    convertBooleanValue,
    convertDateTimeForSubmitting,
    convertStringToList,
    LIMITED_NUMBER_OF_UPLOADED_ITEMS,
} from 'features/export_import/utils'
import { showErrorNotification } from 'features/notifications/notificationsSlice'
// @ts-ignore
import SampleFile from 'features/export_import/sample_files/sample_show.xlsx'
import {
    GroupMappedFields, ImportDataFormProps, MappedField
} from 'features/export_import/types'
import { importShows } from './graphql/mutation'

export const getGroup: () => GroupMappedFields = () => ([
    { id: 1, text: _('show_import.group.general') },
    { id: 2, text: _('show_import.group.virtual_event') },
    { id: 3, text: _('show_import.group.app') },
])

export const mappedFields: MappedField[] = [
    {
        field: 'externalId', label: 'ID', isRequired: true, groupId: 1
    },
    {
        field: 'name', label: 'Title', isRequired: true, groupId: 1
    },
    { field: 'subtitle', label: 'Subtitle', groupId: 3 },
    {
        field: 'startDate', label: 'Start Date', isRequired: true, groupId: 1
    },
    {
        field: 'startTime', label: 'Start Time', isRequired: true, groupId: 1
    },
    {
        field: 'endDate', label: 'End Date', isRequired: true, groupId: 1
    },
    {
        field: 'endTime', label: 'End Time', isRequired: true, groupId: 1
    },
    { field: 'moderatorEmail', label: 'Moderator Email', groupId: 2 },
    { field: 'streamUrl', label: 'Stream Url', groupId: 2 },
    { field: 'embeddedUrl', label: 'Embedded Url', groupId: 2 },
    { field: 'enableChat', label: 'Enable chat', groupId: 2 },
    { field: 'showAttendees', label: 'Show Attendee', groupId: 2 },
    { field: 'virtualSpace', label: 'Virtual space', groupId: 2 },
    { field: 'playbackUrl', label: 'Playback Url', groupId: 2 },
    { field: 'releaseDate', label: 'Release Date', groupId: 2 },
    { field: 'releaseTime', label: 'Release Time', groupId: 2 },
    { field: 'mapItem', label: 'On-site location', groupId: 3 },
    { field: 'tags', label: 'Tags', groupId: 1 },
    { field: 'sponsor', label: 'Sponsor', groupId: 1 },
    { field: 'isParent', label: 'Is Parent', groupId: 3 },
    { field: 'parentId', label: 'Parent Id', groupId: 3 },
    { field: 'color', label: 'Color', groupId: 1 },
    { field: 'description', label: 'Description', groupId: 1 },
    { field: 'performerExternalIds', label: 'Performer Ids', groupId: 1 },
    { field: 'onlyForSegments', label: 'Only For Segments', groupId: 3 },
]

export const validationSchema = Yup.array().of(
    Yup.object().shape({
        externalId: validation.excelExternalId,
        name: Yup.string().required(),
        startDate: Yup.date().typeError('Invalid date').transform(parseDateString).required(),
        startTime: Yup.date().typeError('Invalid time').transform(parseTimeString).required(),
        endDate: Yup.date().typeError('Invalid date').transform(parseDateString).required(),
        endTime: Yup.date().typeError('Invalid time').transform(parseTimeString).required()
            .when(
                ['startDate', 'startTime', 'endDate'],
                // @ts-ignore
                (startDate, startTime, endDate, schema) => schema.test(
                    'endDateGreaterThanStartDate',
                    'EndDate must be greater than startDate',
                    (v) => {
                        let data = {
                            startDate, startTime, endDate, endTime: v
                        }
                        data = convertDateTimeForSubmitting(data)
                        return data.endDate > data.startDate
                    }
                )
            ),
        moderatorEmail: Yup.string().email(),
        streamUrl: validation.url.nullable(),
        embeddedUrl: validation.url.nullable(),
        enableChat: validation.excelBoolean,
        showAttendees: validation.excelBoolean,
        isParent: validation.excelBoolean,
        color: validation.color,
        playbackUrl: validation.url.nullable(),
        releaseDate: Yup.date().nullable().default(null).typeError('Invalid date')
            .transform(parseDateString),
        releaseTime: Yup.date().nullable().default(null).typeError('Invalid time')
            .transform(parseTimeString)
    })
)

export const importProps: Omit<ImportDataFormProps, 'onSubmit'> = {
    mappedFields,
    getGroupMappedFields: getGroup,
    sampleFile: SampleFile,
    validationSchema,
}

export const handleSubmit = async (eventSlug, data, dispatch, callback) => {
    const submittedData = data.map((e) => {
        e.performerExternalIds = convertStringToList(e.performerExternalIds)
        e.onlyForSegments = convertStringToList(e.onlyForSegments)
        e.tags = convertStringToList(e.tags)
        e = convertDateTimeForSubmitting(e)

        if (e.releaseDate && e.releaseTime) {
            e.releaseDate.setHours(e.releaseTime.getHours())
            e.releaseDate.setMinutes(e.releaseTime.getMinutes())
            e.releaseDate.setSeconds(e.releaseTime.getSeconds())
        }
        delete e.releaseTime

        return {
            ...e,
            color: e.color || '#ececec',
            isParent: convertBooleanValue(e.isParent),
            enableChat: convertBooleanValue(e.enableChat),
            showAttendees: convertBooleanValue(e.showAttendees),
        }
    })

    let completed = 0
    let errorItems = []
    const chunked_data = chunk(submittedData, LIMITED_NUMBER_OF_UPLOADED_ITEMS)
    // eslint-disable-next-line no-restricted-syntax
    for (const _data of chunked_data) {
        // eslint-disable-next-line no-await-in-loop
        const response = await importShows(eventSlug, _data)
        if (response.errors.length) {
            return dispatch(showErrorNotification(response.errors))
        }
        const importResult = response.data.importShows
        completed += LIMITED_NUMBER_OF_UPLOADED_ITEMS
        errorItems = [...errorItems, ...importResult.errorItems.map((e) => e.id)]
        dispatch(
            setProgress({
                completed: Math.min(completed, submittedData.length),
                errorItems,
            })
        )
    }
    callback?.()
}
